import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";

import {
  FiArrowLeft,
  FiCheckCircle,
  FiEdit2,
  FiRefreshCw,
  FiSave,
  FiUser,
} from "react-icons/fi";

import { useNavigate, useParams } from "react-router-dom";
import axios from "../../utills/api";

const APPLICATION_STATUSES = [
  "Submitted",
  "Under Review",
  "Approved",
  "Rejected",
  "Withdrawn",
  "Admitted",
];

const ApplicationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [application, setApplication] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [editing, setEditing] = useState(false);

  const [status, setStatus] = useState("");

  const fetchApplication = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError("");

      const response = await axios.get(
        `/applications/${id}`
      );

      const data = response?.data?.data;

      const applicationData =
        data?.application || data;

      setApplication(applicationData);
      setStatus(
        applicationData?.status ||
          "Submitted"
      );
    } catch (err) {
      console.error(
        "Application details error:",
        err
      );

      setError(
        err?.response?.data?.message ||
          "Unable to load application."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplication();
  }, [id]);

  const updateApplication = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await axios.put(`/applications/${id}`, {
        status,
      });

      setSuccess(
        "Application updated successfully."
      );

      setEditing(false);

      await fetchApplication();
    } catch (err) {
      console.error(
        "Application update error:",
        err
      );

      setError(
        err?.response?.data?.message ||
          "Unable to update application."
      );
    } finally {
      setSaving(false);
    }
  };

  const getApplicantName = () =>
    application?.applicantName ||
    application?.studentName ||
    application?.lead?.name ||
    "Unknown Applicant";

  const getApplicationNumber = () =>
    application?.applicationNumber ||
    application?.applicationNo ||
    application?.number ||
    "—";

  const getCourse = () =>
    application?.course ||
    application?.program ||
    application?.courseName ||
    "—";

  const getValue = (...values) => {
    for (const value of values) {
      if (
        value !== undefined &&
        value !== null &&
        value !== ""
      ) {
        return value;
      }
    }

    return "—";
  };

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const statusClass = (value) => {
    switch (String(value).toLowerCase()) {
      case "approved":
      case "admitted":
        return "bg-success-subtle text-success";

      case "rejected":
      case "withdrawn":
        return "bg-danger-subtle text-danger";

      case "under review":
        return "bg-warning-subtle text-warning";

      default:
        return "bg-primary-subtle text-primary";
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody className="text-center py-5">
              <Spinner className="me-2" />
              Loading application...
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">
            {error || "Application not found."}
          </Alert>

          <Button
            color="light"
            onClick={() =>
              navigate("/applications")
            }
          >
            <FiArrowLeft
              size={15}
              className="me-1"
            />
            Back to Applications
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Header */}
        <Row className="align-items-center mb-4">
          <Col>
            <Button
              color="light"
              className="mb-3"
              onClick={() =>
                navigate("/applications")
              }
            >
              <FiArrowLeft
                size={15}
                className="me-1"
              />
              Applications
            </Button>

            <div className="d-flex align-items-center">
              <div className="avatar-md">
                <div className="avatar-title rounded-circle bg-primary-subtle text-primary fs-3">
                  <FiUser />
                </div>
              </div>

              <div className="ms-3">
                <h4 className="mb-1">
                  {getApplicantName()}
                </h4>

                <div className="text-muted">
                  Application #
                  {getApplicationNumber()}
                </div>
              </div>
            </div>
          </Col>

          <Col
            xs="12"
            md="auto"
            className="mt-3 mt-md-0"
          >
            <div className="d-flex gap-2">
              <Button
                color="light"
                onClick={fetchApplication}
                disabled={loading}
              >
                <FiRefreshCw
                  size={15}
                  className="me-1"
                />
                Refresh
              </Button>

              {!editing ? (
                <Button
                  color="primary"
                  onClick={() =>
                    setEditing(true)
                  }
                >
                  <FiEdit2
                    size={15}
                    className="me-1"
                  />
                  Edit
                </Button>
              ) : (
                <Button
                  color="success"
                  onClick={updateApplication}
                  disabled={saving}
                >
                  {saving ? (
                    <Spinner
                      size="sm"
                      className="me-2"
                    />
                  ) : (
                    <FiSave
                      size={15}
                      className="me-1"
                    />
                  )}
                  Save
                </Button>
              )}
            </div>
          </Col>
        </Row>

        {error && (
          <Alert
            color="danger"
            toggle={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            color="success"
            toggle={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* Application Summary */}
        <Row>
          <Col xl="8">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="card-title mb-1">
                      Application Information
                    </h5>

                    <p className="text-muted mb-0">
                      Basic application details
                    </p>
                  </div>

                  {!editing && (
                    <Badge
                      className={statusClass(
                        application.status
                      )}
                    >
                      {application.status ||
                        "Submitted"}
                    </Badge>
                  )}
                </div>

                <Row className="g-4">
                  <Col md="6">
                    <Label className="text-muted mb-1">
                      Applicant
                    </Label>

                    <div className="fw-medium">
                      {getApplicantName()}
                    </div>
                  </Col>

                  <Col md="6">
                    <Label className="text-muted mb-1">
                      Application Number
                    </Label>

                    <div className="fw-medium">
                      {getApplicationNumber()}
                    </div>
                  </Col>

                  <Col md="6">
                    <Label className="text-muted mb-1">
                      Course / Program
                    </Label>

                    <div className="fw-medium">
                      {getCourse()}
                    </div>
                  </Col>

                  <Col md="6">
                    <Label className="text-muted mb-1">
                      Application Date
                    </Label>

                    <div className="fw-medium">
                      {formatDate(
                        application.applicationDate ||
                          application.createdAt
                      )}
                    </div>
                  </Col>

                  <Col md="6">
                    <Label className="text-muted mb-1">
                      Status
                    </Label>

                    {editing ? (
                      <Input
                        type="select"
                        value={status}
                        onChange={(event) =>
                          setStatus(
                            event.target.value
                          )
                        }
                      >
                        {APPLICATION_STATUSES.map(
                          (item) => (
                            <option
                              key={item}
                              value={item}
                            >
                              {item}
                            </option>
                          )
                        )}
                      </Input>
                    ) : (
                      <div>
                        <Badge
                          className={statusClass(
                            application.status
                          )}
                        >
                          {application.status ||
                            "Submitted"}
                        </Badge>
                      </div>
                    )}
                  </Col>

                  <Col md="6">
                    <Label className="text-muted mb-1">
                      Assigned To
                    </Label>

                    <div className="fw-medium">
                      {getValue(
                        application.assignedTo?.name,
                        application.assignedToName,
                        application.counsellor?.name,
                        application.counsellorName
                      )}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Contact */}
            <Card>
              <CardBody>
                <h5 className="card-title mb-4">
                  Applicant Contact
                </h5>

                <Row className="g-4">
                  <Col md="6">
                    <Label className="text-muted mb-1">
                      Email
                    </Label>

                    <div>
                      {getValue(
                        application.email,
                        application.lead?.email
                      )}
                    </div>
                  </Col>

                  <Col md="6">
                    <Label className="text-muted mb-1">
                      Phone
                    </Label>

                    <div>
                      {getValue(
                        application.phone,
                        application.mobile,
                        application.lead?.phone,
                        application.lead?.mobile
                      )}
                    </div>
                  </Col>

                  <Col md="6">
                    <Label className="text-muted mb-1">
                      Date of Birth
                    </Label>

                    <div>
                      {formatDate(
                        application.dateOfBirth ||
                          application.dob
                      )}
                    </div>
                  </Col>

                  <Col md="6">
                    <Label className="text-muted mb-1">
                      Gender
                    </Label>

                    <div>
                      {getValue(
                        application.gender
                      )}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Side Panel */}
          <Col xl="4">
            <Card>
              <CardBody>
                <h5 className="card-title mb-4">
                  Education Details
                </h5>

                <div className="mb-3">
                  <Label className="text-muted mb-1">
                    Qualification
                  </Label>

                  <div>
                    {getValue(
                      application.qualification,
                      application.education
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <Label className="text-muted mb-1">
                    Passing Year
                  </Label>

                  <div>
                    {getValue(
                      application.passingYear
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <Label className="text-muted mb-1">
                    Percentage / Score
                  </Label>

                  <div>
                    {getValue(
                      application.percentage,
                      application.score,
                      application.marks
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-muted mb-1">
                    Previous Institution
                  </Label>

                  <div>
                    {getValue(
                      application.previousInstitution,
                      application.previousSchool,
                      application.previousCollege
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h5 className="card-title mb-4">
                  Application Timeline
                </h5>

                <div className="d-flex mb-3">
                  <div className="avatar-xs">
                    <div className="avatar-title rounded-circle bg-primary-subtle text-primary">
                      <FiCheckCircle
                        size={14}
                      />
                    </div>
                  </div>

                  <div className="ms-2">
                    <div className="fw-medium">
                      Application Created
                    </div>

                    <div className="text-muted small">
                      {formatDate(
                        application.createdAt
                      )}
                    </div>
                  </div>
                </div>

                {application.updatedAt && (
                  <div className="d-flex">
                    <div className="avatar-xs">
                      <div className="avatar-title rounded-circle bg-info-subtle text-info">
                        <FiEdit2
                          size={14}
                        />
                      </div>
                    </div>

                    <div className="ms-2">
                      <div className="fw-medium">
                        Last Updated
                      </div>

                      <div className="text-muted small">
                        {formatDate(
                          application.updatedAt
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ApplicationDetails;